import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWindowClose } from 'react-icons/fa';
import CommonModal from './CommonModal';
import PersonnelModal from './PersonnelModal';
import AdminOrderCancelModal from './AdminOrderCancelModal';
import AdminOrderRefundModal from './AdminOrderRefundModal';
import { catchError, formatWon, maskingInfo } from 'JS/common';
import { getAdminOrderDetail } from 'JS/API';
import { adminOrderStatusArr, adminOrderStatusColor } from 'JS/array';

const AdminOrderDetailModal = ({ merchantUid, onClose, onMutate }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personnelModal, setPersonnelModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);

  const [innerAlertBox, setInnerAlertBox] = useState({
    mode: '',
    context: '',
    bool: false,
  });
  const [innerAlert, setInnerAlert] = useState('');
  const navigate = useNavigate();

  const handleBackgroundClick = e => {
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose();
    }
  };

  const fetchDetail = async () => {
    setLoading(true);
    const result = await getAdminOrderDetail(merchantUid);
    if (typeof result === 'object') {
      setOrder(result?.data?.data || null);
    } else {
      catchError(result, navigate, setInnerAlertBox, setInnerAlert);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantUid]);

  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return dateStr.replace('T', ' ').substring(0, 16);
  };

  const formatPhone = phone => {
    if (!phone) return '';
    return phone.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
  };

  const getPayMethodLabel = method => {
    const methods = {
      card: '카드',
      trans: '계좌이체',
      vbank: '가상계좌',
      phone: '휴대폰',
    };
    return methods[method] || method || '-';
  };

  const buildPersonnelInfo = user => {
    if (!user) return null;
    return {
      id: maskingInfo('id', user.user_id || ''),
      name: maskingInfo('name', user.name || user.user_name || ''),
      department: user.department && user.department !== '' ? user.department : 'none',
      gender: user.gender,
      birth: user.birthday || '',
      phone: user.phone
        ? maskingInfo('phone', formatPhone(user.phone))
        : '',
      email: maskingInfo('email', user.email || user.user_email || ''),
    };
  };

  if (loading || !order) {
    return (
      <div className='modal-background' onClick={handleBackgroundClick}>
        <div className='modal admin-order-detail'>
          <div className='topBar'>
            <h2>결제건 상세</h2>
            <div onClick={onClose}>
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div className='wrap'>
            <div className='loading'>
              {loading ? '로딩 중...' : '결제건 정보를 불러올 수 없습니다.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    merchant_uid,
    merchant_name,
    supply_price,
    vat,
    merchant_price,
    status,
    status_label,
    payment_key,
    pay_method,
    paid_amount,
    paid_at,
    refunded_amount,
    refunded_at,
    refund_reason,
    memo,
    created_at,
    expired_at,
    cancelled_at,
    user,
  } = order;

  const isPending = status === 'Pending';
  const isPaid = status === 'Paid';
  const isRefunded = status === 'Refunded';
  const personnelInfo = buildPersonnelInfo(user);

  return (
    <>
      <div className='modal-background' onClick={handleBackgroundClick}>
        <div className='modal admin-order-detail'>
          <div className='topBar'>
            <h2>결제건 상세</h2>
            <div onClick={onClose}>
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div className='wrap'>
            {/* 헤더: uid + 상태 */}
            <div className='section header-section'>
              <div className='info-row'>
                <span className='label'>주문번호</span>
                <span className='value uid'>{merchant_uid}</span>
              </div>
              <div className='info-row'>
                <span className='label'>상태</span>
                <span
                  className={`status-badge ${adminOrderStatusColor[status] || ''}`}>
                  {status_label || adminOrderStatusArr[status] || status}
                </span>
              </div>
            </div>

            {/* 회원 정보 */}
            <div className='section'>
              <h3>회원 정보</h3>
              {user ? (
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='label'>회원명</span>
                    <span className='value'>
                      {user.name || user.user_name
                        ? maskingInfo('name', user.name || user.user_name)
                        : '-'}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>아이디</span>
                    <span className='value'>
                      {user.user_id ? maskingInfo('id', user.user_id) : '-'}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>이메일</span>
                    <span className='value'>
                      {user.email || user.user_email
                        ? maskingInfo('email', user.email || user.user_email)
                        : '-'}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>연락처</span>
                    <span className='value'>
                      {user.phone
                        ? maskingInfo('phone', formatPhone(user.phone))
                        : '-'}
                    </span>
                  </div>
                  <div className='info-row'>
                    <button
                      className='extend-btn'
                      onClick={() => setPersonnelModal(true)}>
                      회원 상세보기
                    </button>
                  </div>
                </div>
              ) : (
                <div className='no-history'>회원 정보를 불러올 수 없습니다.</div>
              )}
            </div>

            {/* 결제 항목 */}
            <div className='section'>
              <h3>결제 항목</h3>
              <div className='info-grid'>
                <div className='info-row'>
                  <span className='label'>항목명</span>
                  <span className='value'>{merchant_name}</span>
                </div>
                <div className='info-row'>
                  <span className='label'>공급가</span>
                  <span className='value'>{formatWon(supply_price)}</span>
                </div>
                <div className='info-row'>
                  <span className='label'>부가세</span>
                  <span className='value'>{formatWon(vat)}</span>
                </div>
                <div className='info-row'>
                  <span className='label'>총액</span>
                  <span className='value highlight'>
                    {formatWon(merchant_price)}
                  </span>
                </div>
              </div>
            </div>

            {/* 결제 결과 (Paid 이상) */}
            {(isPaid || isRefunded) && (
              <div className='section'>
                <h3>결제 결과</h3>
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='label'>결제수단</span>
                    <span className='value'>
                      {getPayMethodLabel(pay_method)}
                    </span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>결제금액</span>
                    <span className='value'>{formatWon(paid_amount)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>결제일시</span>
                    <span className='value'>{formatDate(paid_at)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>paymentKey</span>
                    <span className='value uid'>{payment_key || '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* 환불 이력 */}
            {isRefunded && (
              <div className='section'>
                <h3>환불 이력</h3>
                <div className='info-grid'>
                  <div className='info-row'>
                    <span className='label'>환불 금액</span>
                    <span className='value'>{formatWon(refunded_amount)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>환불 일시</span>
                    <span className='value'>{formatDate(refunded_at)}</span>
                  </div>
                  <div className='info-row'>
                    <span className='label'>환불 사유</span>
                    <span className='value'>{refund_reason || '-'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* 메타 */}
            <div className='section'>
              <h3>기타</h3>
              <div className='info-grid'>
                <div className='info-row'>
                  <span className='label'>발행일</span>
                  <span className='value'>{formatDate(created_at)}</span>
                </div>
                <div className='info-row'>
                  <span className='label'>결제 가능 기한</span>
                  <span className='value'>{formatDate(expired_at)}</span>
                </div>
                {cancelled_at && (
                  <div className='info-row'>
                    <span className='label'>취소 일시</span>
                    <span className='value'>{formatDate(cancelled_at)}</span>
                  </div>
                )}
                {memo && (
                  <div className='info-row memo-row'>
                    <span className='label'>메모</span>
                    <span className='value memo'>{memo}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 액션 */}
            <div className='footer'>
              <div className='btn-group'>
                {isPending && (
                  <button
                    className='refund-btn'
                    onClick={() => setCancelModal(true)}>
                    발행 취소
                  </button>
                )}
                {isPaid && (
                  <button
                    className='refund-btn'
                    onClick={() => setRefundModal(true)}>
                    환불
                  </button>
                )}
                <button className='close-btn' onClick={onClose}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {personnelModal && personnelInfo && (
        <PersonnelModal
          setEditModal={setPersonnelModal}
          info={personnelInfo}
        />
      )}

      {cancelModal && (
        <AdminOrderCancelModal
          merchantUid={merchant_uid}
          onClose={() => setCancelModal(false)}
          onCancelled={() => {
            setCancelModal(false);
            fetchDetail();
            if (onMutate) onMutate();
          }}
        />
      )}

      {refundModal && (
        <AdminOrderRefundModal
          merchantUid={merchant_uid}
          paidAmount={paid_amount || merchant_price}
          onClose={() => setRefundModal(false)}
          onRefunded={() => {
            setRefundModal(false);
            fetchDetail();
            if (onMutate) onMutate();
          }}
        />
      )}

      {innerAlertBox.bool && (
        <CommonModal
          setModal={setInnerAlertBox}
          modal={innerAlertBox}
          okFn={() => {
            if (innerAlert === 'logout') navigate('/');
          }}
        />
      )}
    </>
  );
};

export default AdminOrderDetailModal;
